import {
	useLocalizedPathnames,
	useLoggedIn,
	useLoggedOut,
} from '@/components/user-context/helpers/cohorts';
import type { Experiment } from '@/utils/experiment/use-experiment';

export type UniformExperimentConfigParameters = {
	experimentId: string;
	parameterName?: string;
	valueType?: UniformExperimentValueType;
	treatmentValues?: string;
	targetedPathnames?: string;
	audience?: UniformExperimentAudience;
	deferExposure?: boolean;
	description?: string;
	expectedReleaseDate?: string;
	expectedRuntime?: string;
};

export type UniformExperimentValue = boolean | string;

export type UniformExperimentValueType = 'boolean' | 'string';

export type UniformExperimentAudience = 'all' | 'loggedOut' | 'loggedIn';

export const CONTROL_VALUE = 'control';

export function useUniformExperimentConfig(
	args: UniformExperimentConfigParameters,
): Experiment<UniformExperimentValue> {
	const {
		experimentId,
		parameterName = 'isEnabled',
		valueType = 'boolean',
		treatmentValues = '',
		targetedPathnames = '',
		audience = 'all',
		deferExposure = false,
		description = `Experiment configured in Uniform (${experimentId})`,
		expectedReleaseDate = 'unknown - configured in Uniform',
		expectedRuntime = 'unknown - configured in Uniform',
	} = args;

	const targetedPathnameList = parseCommaSeparatedList(targetedPathnames);
	const treatmentValueList = parseCommaSeparatedList(treatmentValues);

	const matchesTargetedPathname = useLocalizedPathnames(targetedPathnameList);
	const isLoggedOut = useLoggedOut();
	const isLoggedIn = useLoggedIn();

	const hasExperimentId = experimentId.length > 0;
	const matchesAudience = resolveAudienceMatch({ audience, isLoggedIn, isLoggedOut });
	const matchesPathname = targetedPathnameList.length === 0 || matchesTargetedPathname;

	const isInCohort = resolveCohort({ hasExperimentId, matchesAudience, matchesPathname });

	return {
		experimentName: experimentId,
		parameterName: parameterName || 'isEnabled',
		defaultValue: valueType === 'string' ? CONTROL_VALUE : false,
		options: {
			fireExperimentExposure: !deferExposure && hasExperimentId,
			typeGuard: createTypeGuard({ valueType, treatmentValueList }),
		},
		targeting: {
			cohort: () => isInCohort,
		},
		metadata: {
			description,
			expectedReleaseDate,
			expectedRuntime,
			context: {
				urls: targetedPathnameList,
			},
		},
	};
}

export type IsTreatmentValueArgs = {
	value: unknown;
	valueType?: UniformExperimentValueType;
	treatmentValues?: string;
};

export function isTreatmentValue(args: IsTreatmentValueArgs): boolean {
	const { value, valueType = 'boolean', treatmentValues } = args;

	if (valueType === 'boolean') {
		return value === true;
	}

	if (typeof value !== 'string' || value.length === 0) {
		return false;
	}

	const treatmentValueList = parseCommaSeparatedList(treatmentValues);

	if (treatmentValueList.length > 0) {
		return treatmentValueList.includes(value);
	}

	return value !== CONTROL_VALUE;
}

function parseCommaSeparatedList(value?: string): string[] {
	if (!value) {
		return [];
	}

	return value
		.split(',')
		.map((entry) => entry.trim())
		.filter((entry) => entry.length > 0);
}

type CreateTypeGuardArgs = {
	valueType: UniformExperimentValueType;
	treatmentValueList: string[];
};

function createTypeGuard(
	args: CreateTypeGuardArgs,
): (value: unknown) => value is UniformExperimentValue {
	const { valueType, treatmentValueList } = args;

	if (valueType === 'boolean') {
		return (value: unknown): value is UniformExperimentValue => typeof value === 'boolean';
	}

	return (value: unknown): value is UniformExperimentValue => {
		if (typeof value !== 'string') {
			return false;
		}

		if (treatmentValueList.length === 0) {
			return true;
		}

		return value === CONTROL_VALUE || treatmentValueList.includes(value);
	};
}

type ResolveAudienceMatchArgs = {
	audience: UniformExperimentAudience;
	isLoggedIn: boolean | undefined;
	isLoggedOut: boolean | undefined;
};

function resolveAudienceMatch(args: ResolveAudienceMatchArgs): boolean | undefined {
	const { audience, isLoggedIn, isLoggedOut } = args;

	if (audience === 'loggedOut') {
		return isLoggedOut;
	}

	if (audience === 'loggedIn') {
		return isLoggedIn;
	}

	return true;
}

type ResolveCohortArgs = {
	hasExperimentId: boolean;
	matchesAudience: boolean | undefined;
	matchesPathname: boolean;
};

function resolveCohort(args: ResolveCohortArgs): boolean | undefined {
	const { hasExperimentId, matchesAudience, matchesPathname } = args;

	if (!hasExperimentId) {
		return false;
	}

	if (matchesAudience === undefined) {
		return undefined;
	}

	return matchesAudience && matchesPathname;
}
